import * as React from 'react';

import { useLockFn } from 'ahooks';
import { Flex, Form, notification } from 'antd';
import { useNavigate } from 'react-router-dom';

import { PrimitiveForm } from 'components';
import { usePurchaseValue } from 'context';
import { updatePurchase } from 'services';
import {
  fixedParseFloat,
  generatePurchaseProductInvoice,
  PATHNAME,
  preciseRound,
} from 'utils';

import { PurchaseFormAction } from './action';
import { PurchaseFormTabs } from './tabs';

const {
  LOCAL: {
    PURCHASES: { BASE_URL: PURCHASES_BASE_URL },
  },
} = PATHNAME;

type FieldType = Pick<ReturnType<typeof usePurchaseValue>, 'status'> &
  Partial<Record<string, string | number>>;

const PurchaseForm = () => {
  const [form] = Form.useForm<FieldType>();
  const {
    id = '',
    trackingCode = '',
    purchaseProducts = [],
    defaultStatus,
    canSubmit,
    mode,
  } = usePurchaseValue();
  const navigate = useNavigate();

  const onFinish = useLockFn(async ({ status = defaultStatus! }: FieldType) => {
    const params: Parameters<typeof updatePurchase>[0] = {
      id,
    };
    const body: Parameters<typeof updatePurchase>[1] = {
      status,
      purchaseProducts: purchaseProducts.map(
        ({ id, packagingInfo: { amount: packagingInfoAmount }, amount }) => ({
          id,
          size: preciseRound(amount, packagingInfoAmount),
        }),
      ),
    };

    switch (mode) {
      case 'Update': {
        return updatePurchase(params, body)
          .then(() => {
            notification.success({
              message: `سفارش ${trackingCode} با موفقیت ویرایش شد.`,
            });
            navigate(PURCHASES_BASE_URL);
          })
          .catch(message => {
            notification.error({ message });
          });
      }
      default:
        break;
    }
  });

  React.useEffect(() => {
    if (canSubmit) {
      form.submit();
    }
  }, [canSubmit, form]);

  return (
    <PrimitiveForm
      form={form}
      fields={[
        ...purchaseProducts.flatMap(purchaseProduct => {
          const { id, price, originalPrice, size } = purchaseProduct;
          const { originalPrice: totalOriginalPrice, price: totalPrice } =
            generatePurchaseProductInvoice(purchaseProduct, 'amount');

          return [
            {
              name: `product-main-price-${id}`,
              value: fixedParseFloat(originalPrice / size),
            },
            {
              name: `product-unit-discount-${id}`,
              value: fixedParseFloat((originalPrice - price) / size),
            },
            {
              name: `product-total-price-${id}`,
              value: totalOriginalPrice,
            },
            {
              name: `product-total-discount-${id}`,
              value: fixedParseFloat(totalOriginalPrice - totalPrice),
            },
          ];
        }),
        ...purchaseProducts.map(
          ({ id, packagingInfo: { amount: packagingInfoAmount }, amount }) => ({
            name: `product-total-size-${id}`,
            value: preciseRound(amount, packagingInfoAmount),
          }),
        ),
        ...purchaseProducts.map(({ id, amount }) => ({
          name: `product-total-amount-${id}`,
          value: amount,
        })),
      ]}
      onFinish={onFinish}
    >
      <Flex
        className='*:rounded *:bg-white max-xl:flex-col max-xl:last:*:sticky max-xl:last:*:inset-x-0 max-xl:last:*:bottom-0 max-xl:last:*:z-10 max-xl:last:*:shadow-inner xl:items-start xl:first:*:basis-[70%] xl:last:*:basis-[30%]'
        gap='large'
      >
        <PurchaseFormTabs />
        <PurchaseFormAction />
      </Flex>
    </PrimitiveForm>
  );
};

export { PurchaseForm };
